<script setup lang="ts">
import type { Component } from 'vue';
import { computed } from 'vue';

import * as components from './icons';
import type {
  AppSvgIconColor,
  AppSvgIconRotate,
  AppSvgIconSize,
  AppSvgIconTranslate,
  AppSvgIconType,
  AppSvgIconTransformedProps,
} from './AppSvgIcon.model';
import { transformPropsFormat } from './AppSvgIcon.model';

interface AppSvgIconInnerProps {
  name: AppSvgIconType;
  translate?: AppSvgIconTranslate;
  rotate?: AppSvgIconRotate;
  size?: AppSvgIconSize;
  color?: AppSvgIconColor;
  ariaLabel?: string;
}

const props = defineProps<AppSvgIconInnerProps>();

const settings = computed<AppSvgIconTransformedProps>(() =>
  transformPropsFormat(
    props.name,
    props.translate,
    props.rotate,
    props.size,
    props.color
  )
);

const ICONS: Record<AppSvgIconType, Component> = {
  accommodation: components.Accommodation,
  activity: components.Activity,
  activityOutlined: components.ActivityOutlined,
  adultsOnly: components.AdultsOnly,
  ai: components.Ai,
  airConditioning: components.AirConditioning,
  airplane: components.Airplane,
  airplaneLanding: components.AirplaneLanding,
  airplaneTakeoff: components.AirplaneTakeoff,
  allInclusive: components.AllInclusive,
  arrowLeft: components.ArrowLeft,
  arrowRight: components.ArrowRight,
  arrowUpDouble: components.ArrowUpDouble,
  bagCabin: components.BagCabin,
  bagChecked: components.BagChecked,
  bagUnavailable: components.BagUnavailable,
  balcony: components.Balcony,
  bar: components.Bar,
  bed: components.Bed,
  bedOutlined: components.BedOutlined,
  bedOutlinedCrossed: components.BedOutlinedCrossed,
  boutique: components.Boutique,
  building: components.Building,
  calendarOutlined: components.CalendarOutlined,
  car: components.Car,
  carOutlined: components.CarOutlined,
  carpet: components.Carpet,
  cart: components.Cart,
  case: components.Case,
  castle: components.Castle,
  ceilingFan: components.CeilingFan,
  chair: components.Chair,
  change: components.Change,
  check: components.Check,
  chevronDown: components.ChevronDown,
  chevronLeft: components.ChevronLeft,
  chevronRight: components.ChevronRight,
  chevronUp: components.ChevronUp,
  childBeds: components.ChildBeds,
  childChairs: components.ChildChairs,
  clockOutlined: components.ClockOutlined,
  close: components.Close,
  closeInCircle: components.CloseInCircle,
  code: components.Code,
  concierge: components.Concierge,
  coffeeMaker: components.CoffeeMaker,
  conferenceRoom: components.ConferenceRoom,
  creditCard: components.CreditCard,
  desk: components.Desk,
  dishwasher: components.Dishwasher,
  distance: components.Distance,
  door: components.Door,
  drag: components.Drag,
  electricCarCharger: components.ElectricCarCharger,
  engineHeater: components.EngineHeater,
  eye: components.Eye,
  family: components.Family,
  ferry: components.Ferry,
  fileList: components.FileList,
  filter: components.Filter,
  filterAlt: components.FilterAlt,
  fireplace: components.Fireplace,
  food: components.Food,
  freezer: components.Freezer,
  gallery: components.Gallery,
  garage: components.Garage,
  garden: components.Garden,
  golf: components.Golf,
  gym: components.Gym,
  hairDryer: components.HairDryer,
  halfStar: components.HalfStar,
  heart: components.Heart,
  heartOutlined: components.HeartOutlined,
  homeOutlined: components.HomeOutlined,
  infoCircle: components.InfoCircle,
  internet: components.Internet,
  iron: components.Iron,
  itinerary: components.Itinerary,
  jacuzzi: components.Jacuzzi,
  kettle: components.Kettle,
  lakeView: components.LakeView,
  layersOutlined: components.LayersOutlined,
  lightbulb: components.Lightbulb,
  link: components.Link,
  list: components.List,
  locationMark: components.LocationMark,
  locationMarkOutlined: components.LocationMarkOutlined,
  lookingGlass: components.LookingGlass,
  lowPriceCalendar: components.LowPriceCalendar,
  lockClosed: components.LockClosed,
  lockOpen: components.LockOpen,
  map: components.Map,
  mapFilled: components.MapFilled,
  masonryGallery: components.MasonryGallery,
  microwave: components.Microwave,
  miniBar: components.MiniBar,
  minus: components.Minus,
  money: components.Money,
  moon: components.Moon,
  mop: components.Mop,
  mountainView: components.MountainView,
  nearMe: components.NearMe,
  nearMeOutlined: components.NearMeOutlined,
  neighbourhood: components.Neighbourhood,
  newTab: components.NewTab,
  noPets: components.NoPets,
  noSmoking: components.NoSmoking,
  noTransport: components.NoTransport,
  oneWayFlight: components.OneWayFlight,
  oven: components.Oven,
  parking: components.Parking,
  pencil: components.Pencil,
  people: components.People,
  peopleOutlined: components.PeopleOutlined,
  personBicycling: components.PersonBicycling,
  personWalking: components.PersonWalking,
  pets: components.Pets,
  phone: components.Phone,
  plus: components.Plus,
  pool: components.Pool,
  position: components.Position,
  priceLabel: components.PriceLabel,
  printer: components.Printer,
  quality: components.Quality,
  returnFlight: components.ReturnFlight,
  route: components.Route,
  safetyBox: components.SafetyBox,
  satelliteTv: components.SatelliteTv,
  sauna: components.Sauna,
  save: components.Save,
  seaView: components.SeaView,
  seat: components.Seat,
  seatInCircle: components.SeatInCircle,
  share: components.Share,
  shower: components.Shower,
  ski: components.Ski,
  smoking: components.Smoking,
  sofa: components.Sofa,
  spa: components.Spa,
  star: components.Star,
  sun: components.Sun,
  sunbeds: components.Sunbeds,
  sustainable: components.Sustainable,
  tennis: components.Tennis,
  terrace: components.Terrace,
  threeDotsHorizontal: components.ThreeDotsHorizontal,
  searchOff: components.SearchOff,
  threeDotsVertical: components.ThreeDotsVertical,
  thumbUp: components.ThumbUp,
  ticket: components.Ticket,
  toaster: components.Toaster,
  trashCanEmpty: components.TrashCanEmpty,
  trashCanOutlined: components.TrashCanOutlined,
  tune: components.Tune,
  tv: components.Tv,
  unavailable: components.Unavailable,
  vineyard: components.Vineyard,
  warningCircle: components.WarningCircle,
  washingMachine: components.WashingMachine,
  zoom: components.Zoom,
};

const iconComponent = computed(() => ICONS[props.name]);
</script>

<template>
  <component
    :is="iconComponent"
    :width="settings.width"
    :height="settings.height"
    :viewBox="settings.viewBox"
    :style="settings.style"
    :class="props.name"
    :aria-label="props.ariaLabel"
  />
</template>

<style scoped lang="scss">
@media print {
  svg {
    // NOTE: Forces icons to be dark in PDF document preview
    fill: var(--color-neutral-70) !important;
  }
}
</style>
